import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const steps = [
  { day: "Day 1–2", en: "Script Development", zh: "剧本开发", desc: "AI-assisted trend analysis & high-potential story direction identification.", icon: "📝" },
  { day: "Day 2–4", en: "AI-Assisted Planning", zh: "AI辅助规划", desc: "Scene planning, storyboarding & production optimization powered by AI.", icon: "🤖" },
  { day: "Day 4–8", en: "Filming & Production", zh: "实拍制作", desc: "Local actors, authentic environments — real production for emotional authenticity.", icon: "🎥" },
  { day: "Day 8–11", en: "Post-Production", zh: "后期剪辑", desc: "AI-enhanced editing, pacing optimization & multi-platform format delivery.", icon: "✂️" },
  { day: "Day 13–14+", en: "Data Feedback", zh: "数据驱动迭代", desc: "Performance analytics & continuous iteration to increase hit probability.", icon: "📊" },
];

const AIProduction = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} style={{ padding: "100px 40px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={visible ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 36, height: 2, background: "#FF1C35" }} />
            <span style={{ color: "#FF1C35", fontSize: 12, fontWeight: 700, letterSpacing: 3 }}>AI PRODUCTION PIPELINE · AI生产体系</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 20, marginBottom: 56 }}>
            <h2 style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 900, lineHeight: 1.1, margin: 0 }}>
              Industrialized Content<br /><span style={{ color: "#FF1C35" }}>Production System</span>
            </h2>
            <div style={{
              padding: "10px 24px", border: "1px solid rgba(255,28,53,0.3)",
              borderRadius: 999, color: "#FF1C35", fontSize: 14, fontWeight: 700,
              background: "rgba(255,28,53,0.06)",
            }}>
              ⚡ 7–14 Day Full Cycle
            </div>
          </div>
        </motion.div>

        {/* Pipeline Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, position: "relative" }}>
          {/* Vertical line */}
          <div style={{
            position: "absolute", left: 95, top: 30, bottom: 30, width: 1,
            background: "linear-gradient(to bottom, transparent, rgba(255,28,53,0.3), transparent)",
          }} />

          {steps.map((s, i) => (
            <motion.div key={s.en}
              initial={{ opacity: 0, x: -20 }}
              animate={visible ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.12, duration: 0.6 }}
              style={{
                display: "flex", alignItems: "flex-start", gap: 24,
                padding: "24px 28px", borderRadius: 14,
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.05)",
                transition: "background 0.3s, border-color 0.3s",
                cursor: "default",
              }}
              whileHover={{ background: "rgba(255,28,53,0.04)", borderColor: "rgba(255,28,53,0.2)" } as never}
            >
              <div style={{ minWidth: 68, textAlign: "right" }}>
                <span style={{ fontSize: 11, color: "#FF1C35", fontWeight: 700, fontFamily: "JetBrains Mono, monospace", letterSpacing: 0.5 }}>{s.day}</span>
              </div>
              <div style={{
                width: 42, height: 42, borderRadius: "50%",
                background: "rgba(255,28,53,0.1)", border: "1px solid rgba(255,28,53,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>
                {s.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{s.en}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>{s.zh}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={visible ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}
          style={{ marginTop: 36, padding: "20px 28px", borderRadius: 12, background: "rgba(255,28,53,0.06)", border: "1px solid rgba(255,28,53,0.15)", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 15, color: "rgba(255,255,255,0.7)", fontStyle: "italic" }}>
            "Unlike purely AI-generated content, our hybrid approach combines AI efficiency with real filming — delivering both scalability and emotional authenticity."
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "rgba(255,255,255,0.3)" }}>不同于纯AI生成内容，我们的模式兼顾规模化能力与真实情绪表达</p>
        </motion.div>
      </div>
    </section>
  );
};

export default AIProduction;
